import { forwardRef, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

const MAX_BY_GRAPHICS = {
  high: 240,
  medium: 128,
  low: 48,
} as const

export interface BurstHandle {
  burst: (pos: [number, number, number], color: string, count: number, spread: number, power: number) => void
}

interface Particle {
  life: number
  maxLife: number
  active: boolean
  vx: number
  vy: number
  vz: number
}

export const Particles = forwardRef<BurstHandle, { reducedMotion: boolean; graphics: 'high' | 'medium' | 'low' }>(function Particles({ reducedMotion, graphics }, apiRef) {
  const max = MAX_BY_GRAPHICS[graphics]
  const meshRef = useRef<THREE.InstancedMesh>(null)
  const particles = useMemo<Particle[]>(() => Array.from({ length: max }, () => ({ life: 0, maxLife: 0, active: false, vx: 0, vy: 0, vz: 0 })), [max])
  const positions = useMemo(() => new Float32Array(max * 3), [max])
  const colorArr = useMemo(() => new Float32Array(max * 3), [max])
  const dummy = useMemo(() => new THREE.Object3D(), [])
  const cursor = useRef(0)
  const activeCount = useRef(0)
  const tmpColor = useMemo(() => new THREE.Color(), [])

  useLayoutEffect(() => {
    const mesh = meshRef.current
    if (!mesh || typeof mesh.setMatrixAt !== 'function') return
    dummy.position.set(0, -999, 0)
    dummy.scale.setScalar(0.001)
    dummy.updateMatrix()
    for (let i = 0; i < max; i++) mesh.setMatrixAt(i, dummy.matrix)
    mesh.count = 0
    mesh.instanceMatrix.needsUpdate = true
  }, [dummy, max])

  useImperativeHandle(apiRef, () => ({
    burst(pos, color, count, spread, power) {
      if (graphics === 'low') count = Math.min(count, reducedMotion ? 3 : 6)
      else if (reducedMotion) count = Math.min(count, 6)
      tmpColor.set(color)
      for (let i = 0; i < count; i++) {
        const idx = cursor.current % max
        cursor.current++
        const p = particles[idx]
        if (!p.active) activeCount.current++
        p.active = true
        p.life = 1
        p.maxLife = 0.5 + Math.random() * 0.5
        const ang = Math.random() * Math.PI * 2
        const up = 0.4 + Math.random() * power
        p.vx = Math.cos(ang) * spread * (0.5 + Math.random())
        p.vz = Math.sin(ang) * spread * (0.5 + Math.random())
        p.vy = up
        positions[idx * 3] = pos[0]
        positions[idx * 3 + 1] = pos[1]
        positions[idx * 3 + 2] = pos[2]
        colorArr[idx * 3] = tmpColor.r
        colorArr[idx * 3 + 1] = tmpColor.g
        colorArr[idx * 3 + 2] = tmpColor.b
        if (meshRef.current) meshRef.current.count = Math.max(meshRef.current.count, idx + 1)
      }
    },
  }), [colorArr, graphics, max, particles, positions, reducedMotion, tmpColor])

  useFrame((_, delta) => {
    const mesh = meshRef.current
    if (!mesh) return
    if (activeCount.current <= 0) return
    const dt = Math.min(delta, 0.05)
    for (let i = 0; i < max; i++) {
      const p = particles[i]
      if (!p.active) continue
      if (p.life <= 0) {
        p.active = false
        activeCount.current = Math.max(0, activeCount.current - 1)
        dummy.position.set(0, -999, 0)
        dummy.scale.setScalar(0.001)
        dummy.updateMatrix()
        mesh.setMatrixAt(i, dummy.matrix)
        continue
      }
      p.life -= dt / p.maxLife
      if (p.life <= 0) {
        p.active = false
        activeCount.current = Math.max(0, activeCount.current - 1)
        dummy.position.set(0, -999, 0)
        dummy.scale.setScalar(0.001)
        dummy.updateMatrix()
        mesh.setMatrixAt(i, dummy.matrix)
        continue
      }
      p.vy -= 2.4 * dt
      positions[i * 3] += p.vx * dt
      positions[i * 3 + 1] += p.vy * dt
      positions[i * 3 + 2] += p.vz * dt
      const scale = Math.max(0.001, 0.14 * p.life)
      dummy.position.set(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2])
      dummy.scale.setScalar(scale)
      dummy.updateMatrix()
      mesh.setMatrixAt(i, dummy.matrix)
      mesh.setColorAt(i, tmpColor.setRGB(colorArr[i * 3], colorArr[i * 3 + 1], colorArr[i * 3 + 2]))
    }
    mesh.instanceMatrix.needsUpdate = true
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
    if (activeCount.current <= 0) mesh.count = 0
  })

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, max]}>
      <icosahedronGeometry args={[1, 0]} />
      <meshBasicMaterial toneMapped={false} transparent opacity={0.95} />
    </instancedMesh>
  )
})
